import React from 'react';
import { TeamLogo } from '../common/TeamLogo';
import MatchPressureBar from './MatchPressureBar';
import MatchConnectionStatus from './MatchConnectionStatus';
import { groupScorerLines } from '../../utils/matchFeed';

const getTeamShortName = (team, fallback) => (
    team?.shortTitle || team?.shortName || team?.name || fallback
);

/**
 * Sticky scoreboard: crests, score, live minute chip, scorer lines and the
 * live pressure bar. Compacts on scroll so the feed keeps the
 * match state on screen without eating half the viewport.
 */
function MatchHeader({
    teamOne,
    teamTwo,
    score,
    statusChip,
    scorers = { home: [], away: [] },
    pressure = null,
    isCompact = false,
    metaText,
    connectionStatus,
    isLiveMatch,
    homeIsManaged = false,
    awayIsManaged = false
}) {
    const homeName = getTeamShortName(teamOne, 'Home Team');
    const awayName = getTeamShortName(teamTwo, 'Away Team');
    const hasScorers = scorers.home.length > 0 || scorers.away.length > 0;

    return (
        <header
            className={`mx-header${isCompact ? ' mx-header--compact' : ''}`}
            data-testid="match-header"
        >
            <div className="mx-header__inner">
                <div className="mx-header__top">
                    <span className="mx-header__spacer" aria-hidden="true" />
                    <div className="mx-header__meta">{metaText}</div>
                    <MatchConnectionStatus status={connectionStatus} isLiveMatch={isLiveMatch} />
                </div>

                <div className="mx-scorebar">
                    <div className="mx-team">
                        <span className="mx-team__crest">
                            <TeamLogo logoUrl={teamOne?.logo} teamName={teamOne?.name || 'Home Team'} />
                        </span>
                        <span className="mx-team__name">{homeName}</span>
                        {homeIsManaged && <span className="mx-team__tag">Your Team</span>}
                        {!homeIsManaged && awayIsManaged && (
                            <span className="mx-team__tag mx-team__tag--opponent">Opponent</span>
                        )}
                    </div>

                    <div className="mx-scoremid">
                        {/* Rendered as one text node on purpose: the score reads as a
                            single value to assistive tech and to text queries. */}
                        <div className={`mx-score${score.isPlaceholder ? ' mx-score--pre' : ''}`}>
                            {score.isPlaceholder ? '-' : `${score.home} - ${score.away}`}
                        </div>
                        <span className={`mx-chip mx-chip--${statusChip.tone}`} data-testid="match-status-chip">
                            {statusChip.tone === 'live' && <span className="mx-chip__pulse" aria-hidden="true" />}
                            {statusChip.text}
                        </span>
                    </div>

                    <div className="mx-team">
                        <span className="mx-team__crest">
                            <TeamLogo logoUrl={teamTwo?.logo} teamName={teamTwo?.name || 'Away Team'} />
                        </span>
                        <span className="mx-team__name">{awayName}</span>
                        {awayIsManaged && <span className="mx-team__tag">Your Team</span>}
                        {!awayIsManaged && homeIsManaged && (
                            <span className="mx-team__tag mx-team__tag--opponent">Opponent</span>
                        )}
                    </div>
                </div>

                {hasScorers && (
                    <div className="mx-scorers" data-testid="match-scorers">
                        <div className="mx-scorers__side mx-scorers__side--home">
                            {groupScorerLines(scorers.home).map((line) => (
                                <div key={line.name}>
                                    <span className="mx-scorers__ball" aria-hidden="true" />
                                    {line.text}
                                </div>
                            ))}
                        </div>
                        <div className="mx-scorers__side mx-scorers__side--away">
                            {groupScorerLines(scorers.away).map((line) => (
                                <div key={line.name}>
                                    <span className="mx-scorers__ball" aria-hidden="true" />
                                    {line.text}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                <MatchPressureBar pressure={pressure} isLive={isLiveMatch} />
            </div>
        </header>
    );
}

export default MatchHeader;
